import compact from 'lodash/compact'
import map from 'lodash/map'
import size from 'lodash/size'
import { useRouter } from 'next/router'
import Script from 'next/script'
import React, { memo, useContext, useMemo, useState } from 'react'

import { generateProductLinkingDataImage } from '@Components/Image'
import { ProductSearchContext } from '@Globals/Algolia/Context'
import { MetadataContext } from '@Globals/Metadata'

export const LinkedDataV1 = memo(({
  description,
  title,
}: {
  description: string
  title: string
}) => {
  const { hits, objectIDs } = useContext(ProductSearchContext)
  const [initialObjectIDs] = useState(objectIDs)

  const { asPath: currentRouterPath } = useRouter()
  const { store: { config: { currencyCode = '' } = {} } = {}, storeURL } = useContext(MetadataContext)

  const json = useMemo((): string | null => {
    if (!hits || !size(hits) || !initialObjectIDs || !size(initialObjectIDs)) {
      return null
    }

    const jsonLDData = compact(map(initialObjectIDs, objectID => hits[objectID]))

    const dataObject = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      url: storeURL(currentRouterPath),
      name: title,
      description,
      itemListElement: map(
        jsonLDData,
        (product, productIndex) => ({
          '@type': 'ListItem',
          // For some reason, the index comes out as a string even though it's typed as a number? Weird.
          position: Number(productIndex) + 1,
          item: {
            '@type': 'Product',
            name: product.title,
            brand: {
              '@type': 'Brand',
              name: product.brandTitle,
            },
            category: product.category || '',
            url: storeURL(product.url),
            sku: product.id,
            image: generateProductLinkingDataImage(product.primaryImage ?? ''),
            offers: {
              '@type': 'Offer',
              availability: `https://schema.org/${product.isOutOfStock ? 'OutOfStock' : 'InStock'}`,
              price: product.priceValue,
              priceCurrency: currencyCode,
            },
          },
        }),
      ),
    }

    return JSON.stringify(dataObject)
  }, [hits, initialObjectIDs, storeURL, currentRouterPath, title, description, currencyCode])

  if (!json) { return null }

  return (
    <Script
      dangerouslySetInnerHTML={{ __html: json }}
      id={`category-json-ld-${title}`}
      type="application/ld+json"
    />
  )
})
